import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";
import heroCarousel1 from "@/assets/hero-carousel-1.webp";
import heroCarousel2 from "@/assets/hero-carousel-2.webp";
import heroCarousel3 from "@/assets/hero-carousel-3.webp";
import heroCarousel1Avif from "@/assets/hero-carousel-1.avif";
import heroCarousel2Avif from "@/assets/hero-carousel-2.avif";
import heroCarousel3Avif from "@/assets/hero-carousel-3.avif";

const HERO_SLIDES = [
  { src: heroCarousel1, avif: heroCarousel1Avif },
  { src: heroCarousel2, avif: heroCarousel2Avif },
  { src: heroCarousel3, avif: heroCarousel3Avif },
];

const AUTOPLAY_MS = 9000;
const TRANSITION_DURATION_MS = 1100;

export type HeroBackdropTint = "subtle" | "soft" | "medium" | "strong" | "vivid";
export type HeroBackdropBlend =
  | "normal"
  | "multiply"
  | "screen"
  | "overlay"
  | "soft-light"
  | "color"
  | "hue";

/** Purple wash strength applied via mix-blend-multiply over the photography. */
const TINT_TOKENS: Record<HeroBackdropTint, { primary: string; overlay: string; veil: string }> = {
  subtle: { primary: "bg-primary/15", overlay: "bg-primary/5", veil: "bg-background/55" },
  soft: { primary: "bg-primary/25", overlay: "bg-primary/10", veil: "bg-background/45" },
  medium: { primary: "bg-primary/35", overlay: "bg-primary/15", veil: "bg-background/35" },
  strong: { primary: "bg-primary/55", overlay: "bg-primary/25", veil: "bg-background/30" },
  vivid: { primary: "bg-primary/75", overlay: "bg-primary/40", veil: "bg-background/25" },
};

const BLEND_TOKENS: Record<HeroBackdropBlend, string> = {
  normal: "mix-blend-normal",
  multiply: "mix-blend-multiply",
  screen: "mix-blend-screen",
  overlay: "mix-blend-overlay",
  "soft-light": "mix-blend-soft-light",
  color: "mix-blend-color",
  hue: "mix-blend-hue",
};

interface HeroBackdropProps {
  tint?: HeroBackdropTint;
  blendMode?: HeroBackdropBlend;
  disableVeil?: boolean;
  className?: string;
  /**
   * Increment to reset the carousel back to the first slide.
   * Useful for syncing the backdrop with external events (e.g. search submission).
   */
  resetSignal?: number;
  /** When true, the autoplay timer is paused (e.g. while the user is interacting). */
  paused?: boolean;
}

/**
 * Decorative auto-sliding image backdrop with a configurable purple brand tint.
 * Uses the same Embla carousel sliding motion as the "New on GetPetVet" slider
 * for a consistent feel site-wide. Hidden from assistive tech.
 */
const HeroBackdrop = ({
  tint = "medium",
  blendMode = "multiply",
  disableVeil = false,
  className,
  resetSignal,
  paused = false,
}: HeroBackdropProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    watchDrag: true,
    duration: Math.round(TRANSITION_DURATION_MS / (1000 / 60)), // Embla measures in frames
  });
  const [reduceMotion, setReduceMotion] = useState(false);
  const tokens = TINT_TOKENS[tint];
  const blendClass = BLEND_TOKENS[blendMode];

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Note: the LCP hero image is preloaded at build time via a <link rel="preload">
  // injected into index.html by the `preloadAboveFoldAssets` Vite plugin. That fires
  // during HTML parse - far earlier than any useEffect could - so no runtime hint
  // is needed here.


  // Preload + decode the non-LCP slides so transitions never flicker.
  // Slide 1 is already preloaded via a build-time <link rel="preload"> in
  // index.html (see the `preloadAboveFoldAssets` Vite plugin), so we skip it
  // here. Slides 2 and 3 are deferred to idle time - the autoplay interval
  // is 9s, far longer than any reasonable idle deadline, so the bitmaps are
  // always ready by the time Embla advances. This avoids competing with the
  // carousel's measurement pass and the React hydration work during the
  // FCP-critical window (a forced-reflow source flagged by Lighthouse).
  useEffect(() => {
    const preloadRest = () => {
      HERO_SLIDES.slice(1).forEach((slide) => {
        const img = new Image();
        img.src = slide.avif;
        img.decode?.().catch(() => {
          /* ignore - fallback to normal <img> load */
        });
      });
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(preloadRest, { timeout: 2000 });
    } else {
      // Safari fallback - run after first paint settles.
      const id = window.setTimeout(preloadRest, 1500);
      return () => window.clearTimeout(id);
    }
  }, []);


  useEffect(() => {
    if (!emblaApi || reduceMotion || paused) return;
    const id = window.setInterval(() => {
      emblaApi.scrollNext();
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [emblaApi, reduceMotion, paused]);

  // Reset to the first slide whenever the parent bumps the signal.
  useEffect(() => {
    if (!emblaApi || resetSignal === undefined) return;
    emblaApi.scrollTo(0);
  }, [emblaApi, resetSignal]);

  return (
    <div className={cn("absolute inset-0 overflow-hidden", className)} aria-hidden="true" role="presentation">
      <div className="absolute inset-0 overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {HERO_SLIDES.map((slide, i) => (
            <div key={slide.src} className="relative min-w-0 flex-[0_0_100%] h-full">
              <picture>
                <source srcSet={slide.avif} type="image/avif" />
                <source srcSet={slide.src} type="image/webp" />
                <img
                  src={slide.src}
                  alt=""
                  role="presentation"
                  aria-hidden="true"
                  width={1920}
                  height={1080}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding={i === 0 ? "sync" : "async"}
                  {...({ fetchpriority: i === 0 ? "high" : "low" } as Record<string, string>)}
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ backfaceVisibility: "hidden", transform: "translateZ(0)" }}
                />
              </picture>
            </div>
          ))}
        </div>
      </div>
      {/* Purple brand tint layers (configurable strength + blend) */}
      <div className={cn("absolute inset-0 pointer-events-none", tokens.primary, blendClass)} />
      <div className={cn("absolute inset-0 pointer-events-none", tokens.overlay)} />
      {!disableVeil && (
        <div className={cn("absolute inset-0 pointer-events-none backdrop-blur-[2px]", tokens.veil)} />
      )}
    </div>
  );
};

export default HeroBackdrop;
